import { useFormContext } from 'react-hook-form';
import { Input } from '@knack/asterisk-react';

import { type KnackField } from '@/types/schema/KnackField';
import { type KnackOperator } from '@/types/schema/KnackOperator';
import {
  type SearchViewCriteriaOperator,
  type SearchViewInput
} from '@/types/schema/views/SearchView';
import { useFieldHelpers } from '@/hooks/helpers/useFieldHelpers';
import { ViewFilterInput } from '@/components/views/view-options/filters-inputs/ViewFilterInput';
import { AddressInput } from './AddressInput';
import { AutoIncrementInput } from './AutoIncrementInput';
import { BooleanInput } from './BooleanInput';
import { ConnectionInput } from './ConnectionInput';
import { CurrencyInput } from './CurrencyInput';
import { EquationInput } from './EquationInput';
import { MultipleChoiceInput } from './MultipleChoiceInput';
import { NumberInput } from './NumberInput';
import { PhoneInput } from './PhoneInput';
import { RatingInput } from './RatingInput';
import { TimerInput } from './TimerInput';

type SearchFieldInputProps = {
  field: KnackField;
  intent: 'default' | 'destructive';
  criteriaOperator: SearchViewCriteriaOperator;
  filterRuleIndex: number;
  isDisabled?: boolean;
  input: SearchViewInput;
};

export function SearchFieldInput({
  field,
  intent,
  isDisabled = false,
  filterRuleIndex,
  criteriaOperator,
  input
}: SearchFieldInputProps) {
  const { register } = useFormContext();
  const { isEquationFieldWithDateFormat } = useFieldHelpers();

  const formValueKey = `rules.${filterRuleIndex}.value`;

  if (isDisabled) {
    return <Input className="h-9" disabled intent={intent} {...register(formValueKey)} />;
  }

  if (field.type === 'number') {
    return <NumberInput intent={intent} field={field} formValueKey={formValueKey} />;
  }

  // We render `EquationInput` only if the equation is not in 'date' format.
  // If the field is in 'date' format, it will be handled by the `ViewFilterInput` component below
  if (field.type === 'equation' && !isEquationFieldWithDateFormat(field)) {
    return <EquationInput intent={intent} field={field} formValueKey={formValueKey} />;
  }

  if (field.type === 'currency') {
    return <CurrencyInput field={field} intent={intent} formValueKey={formValueKey} />;
  }

  if (field.type === 'timer') {
    return <TimerInput field={field} intent={intent} formValueKey={formValueKey} />;
  }

  if (field.type === 'auto_increment') {
    return <AutoIncrementInput field={field} intent={intent} formValueKey={formValueKey} />;
  }

  if (field.type === 'phone' && (criteriaOperator === 'is' || criteriaOperator === 'is not')) {
    return <PhoneInput field={field} intent={intent} formValueKey={formValueKey} />;
  }

  if (field.type === 'rating') {
    return <RatingInput field={field} formValueKey={formValueKey} />;
  }

  if (field.type === 'boolean') {
    return <BooleanInput field={field} intent={intent} formValueKey={formValueKey} />;
  }

  if (field.type === 'multiple_choice') {
    return (
      <MultipleChoiceInput
        field={field}
        searchViewFieldInput={input}
        intent={intent}
        formValueKey={formValueKey}
      />
    );
  }

  if (field.type === 'address') {
    return (
      <AddressInput
        formValueKey={formValueKey}
        searchViewCriteriaIndex={filterRuleIndex}
        searchViewCriteriaOperator={criteriaOperator}
      />
    );
  }

  if (field.type === 'connection') {
    return (
      <ConnectionInput
        field={field}
        searchViewFieldInput={input}
        intent={intent}
        formValueKey={formValueKey}
      />
    );
  }

  return (
    <ViewFilterInput
      field={field}
      // At this point, we've already handled all the SearchViewCriteriaOperator
      // so, we can assume that is a KnackOperator, and fallback to the ViewFilterInput
      criteriaOperator={criteriaOperator as KnackOperator}
      criteriaIndex={filterRuleIndex}
      intent={intent}
    />
  );
}
